import {
  EXPERIMENT_GOAL_LABELS,
  type ExperimentGoalId,
} from "@/lib/config/experimentContext";

const OBJECT_GOAL_KEYS = ["id", "goal", "testGoal", "value", "name", "type"] as const;

/**
 * Coerce a backend/draft testGoal into a string ID.
 * Some experiments store a structured object (or other non-string) in this
 * field; UI code that calls `.replace()` / `.toLowerCase()` will crash on those.
 */
export function normalizeTestGoal(value: unknown): string {
  if (value == null || typeof value === "boolean") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeTestGoal(item);
      if (normalized) return normalized;
    }
    return "";
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of OBJECT_GOAL_KEYS) {
      const candidate = record[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return "";
}

/** Human-readable goal for experiment list/detail copy. */
export function formatTestGoalLabel(value: unknown, fallback = ""): string {
  const id = normalizeTestGoal(value);
  if (!id) return fallback;
  if (id in EXPERIMENT_GOAL_LABELS) {
    return EXPERIMENT_GOAL_LABELS[id as ExperimentGoalId];
  }
  return id.replace(/_/g, " ");
}
